import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { regionIndex } from "../lib/api/lidoo.functions";
import { Analytics, ConsentBanner } from "../components/lidoo/consent-banner";
import { I18nProvider, LOCALIZED_LANGS, type Lang } from "../lib/i18n";
import { reportHiggsfieldError } from "../lib/higgsfield-error-reporting";
// Page metadata (browser <title>/favicon + social og: tags) committed into the
// repo by the marketplace meta API and read at BUILD time — no runtime fetch.
// Editing it via the app settings UI rewrites this file and redeploys the app.
import appMetaJson from "../app-meta.json";

declare const __HF_DESIGN_INSPECTOR__: boolean;

// Built-in defaults for any field that isn't set in app-meta.json.
const DEFAULT_TITLE = "Higgsfield App";
const DEFAULT_DESCRIPTION = "Higgsfield Generated Project";

type AppMeta = {
  og_title?: string | null;
  og_description?: string | null;
  og_image_url?: string | null;
  favicon_url?: string | null;
  og_video_url?: string | null;
};

const appMeta = appMetaJson as AppMeta;

// Build the document head (title / description / og: / twitter: / favicon) from
// app-meta.json, falling back to the defaults above for any unset field.
// og_title/og_description double as the browser <title> and meta description;
// og_image_url (when set) also drives the twitter card + image. Built from
// inline tag literals (conditional spreads for the optional image/favicon) so
// it matches the head() shape TanStack expects.
// favicon/og images live in THIS app's own /assets, so the host is never
// inherent. app-meta.json may carry an absolute higgsfield-app URL with a STALE
// host — baked from the app this one was copied/remixed/renamed from — which would
// serve the wrong app's favicon/og. Strip any higgsfield-app host (prod
// higgsfield.app + dev higgsfield-dev.app) down to a root-relative path so it
// always resolves against whoever serves THIS page (preview / prod / custom
// domain). Genuinely external URLs (a CDN image the owner set) are left absolute.
const APP_HOST_ZONES = ["higgsfield.app", "higgsfield-dev.app"];

function toOwnAssetUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  if (value.startsWith("/")) return value; // already root-relative
  try {
    const u = new URL(value);
    const isAppHost = APP_HOST_ZONES.some(
      (zone) => u.hostname === zone || u.hostname.endsWith(`.${zone}`),
    );
    if (isAppHost) return u.pathname + u.search;
    return value; // external host (CDN, etc.) — keep absolute
  } catch {
    return value; // not a parseable URL — leave as-is
  }
}

function buildHead(meta: AppMeta) {
  const title = meta.og_title ?? DEFAULT_TITLE;
  const description = meta.og_description ?? DEFAULT_DESCRIPTION;
  const ogImage = toOwnAssetUrl(meta.og_image_url);
  const favicon = toOwnAssetUrl(meta.favicon_url);
  const ogVideo = toOwnAssetUrl(meta.og_video_url);

  return {
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#F2EFE9" },
      { title },
      { name: "description", content: description },
      { name: "author", content: "sunbedly" },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "sunbedly" },
      { name: "twitter:card", content: ogImage ? "summary_large_image" : "summary" },
      ...(ogImage
        ? [
            { property: "og:image", content: ogImage },
            { name: "twitter:image", content: ogImage },
          ]
        : []),
      // Cover video (og:video) — the animated counterpart of og:image; the
      // Higgsfield feed cards also play it on hover.
      ...(ogVideo ? [{ property: "og:video", content: ogVideo }] : []),
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      // No font preloads, deliberately. On a throttled phone the two woff2
      // files were competing with the hero image for the same bandwidth, and
      // the hero is the LCP. font-display: swap means text simply renders in
      // the fallback face for the first moments instead.
      { rel: "icon", href: favicon ?? "/favicon-32.png", sizes: "32x32" },
      { rel: "icon", href: "/favicon-16.png", sizes: "16x16" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/site.webmanifest" },
    ],
  };
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-[#F2EFE9] px-4">
      <div className="max-w-md text-center">
        <p className="li-mono text-sm text-[#2C4FD6]">404</p>
        <h1 className="li-display mt-2 text-3xl font-black tracking-tighter text-[#1C2430]">
          Page not found
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-[#5B6472]">
          This page does not exist or has been moved.
        </p>
        <Link
          to="/"
          className="li-mono mt-6 inline-block text-[12px] text-[#1C2430] underline decoration-[#2C4FD6] underline-offset-4"
        >
          Back to the beach
        </Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportHiggsfieldError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-[#F2EFE9] px-4">
      <div className="max-w-md text-center">
        <h1 className="li-display text-2xl font-black tracking-tighter text-[#1C2430]">
          This page did not load
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-[#5B6472]">
          Something went wrong on our end. You can try again or head back home.
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-4">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="li-display h-11 rounded-lg bg-[#1C2430] px-5 text-sm font-bold text-[#F2EFE9] active:translate-y-[1px]"
          >
            Try again
          </button>
          <a
            href="/"
            className="li-mono self-center text-[12px] text-[#1C2430] underline decoration-[#2C4FD6] underline-offset-4"
          >
            Back to the beach
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // One query for the whole app: the footer's country and coast links. Loading
  // it here rather than per page means every route gets the same list without
  // the component having to know which route it is rendered under.
  loader: async () => await regionIndex(),
  // Read the committed page metadata at build time (no runtime fetch).
  head: () => buildHead(appMeta),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

// Decides, before first paint, whether to offer the visitor another language.
// The LanguageOffer banner (chrome.tsx) server-renders every language variant
// hidden; this script stamps data-suggest="nl" (etc.) on <html> so exactly one
// is visible in the very first frame. Doing this in React meant the banner
// mounted after hydration and pushed the page down 44 px — that insertion was
// the site's entire cumulative layout shift. A stored manual choice
// (sunbedly_lang) means the visitor already answered; never ask again.
// Kept dependency-free and wrapped in try/catch: if anything here throws, the
// only consequence is no banner.
const LANG_SUGGEST_SCRIPT = `(function(){try{
var L=${JSON.stringify(LOCALIZED_LANGS)};
if(window.localStorage.getItem("sunbedly_lang"))return;
var m=window.location.pathname.match(/^\\/([a-z]{2})(\\/|$)/);
var cur=m&&L.indexOf(m[1])>-1?m[1]:"en";
var nav=(window.navigator.language||"").slice(0,2).toLowerCase();
var g=document.cookie.match(/(?:^|; )sunbedly_lang_geo=([a-z]{2})/);
var s=L.indexOf(nav)>-1?nav:(g&&L.indexOf(g[1])>-1?g[1]:null);
if(s&&s!==cur)document.documentElement.setAttribute("data-suggest",s);
}catch(e){}})();`;

function RootShell({ children }: { children: ReactNode }) {
  // The shell wraps every page, so it has to work out the language the same
  // way the router does. Hardcoding "en" here meant /nl/region/dubai served
  // Dutch copy inside an English document: wrong for screen readers, and it
  // undercuts the hreflang we go to the trouble of emitting.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const m = pathname.match(/^\/([a-z]{2})(\/|$)/);
  const lang = m && LOCALIZED_LANGS.includes(m[1] as Lang) ? m[1] : "en";

  return (
    <html lang={lang} style={{ colorScheme: "light" }}>
      <head>
        <HeadContent />
        {/* Must run before first paint; see LANG_SUGGEST_SCRIPT above. */}
        <script dangerouslySetInnerHTML={{ __html: LANG_SUGGEST_SCRIPT }} />
        {/* Google Analytics used to load right here, on every page, for every
            visitor. That put an `_ga` cookie on the device before anyone was
            asked, which art. 11.7a Telecommunicatiewet does not allow. The tag
            now lives in <Analytics/>, which injects it from the browser and
            only after a yes. Nothing analytics-related is in this document. */}
      </head>
      <body className="sunbedly-body">
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Derived from LOCALIZED_LANGS rather than a hand-written list: an earlier
  // hardcoded /(nl|it|de|fr)/ here silently left Spanish pages server-rendering
  // in English while their <title> was already Spanish.
  const prefixMatch = pathname.match(/^\/([a-z]{2})(\/|$)/);
  // ?lang= is the escape hatch for pages that cannot carry a locale in the
  // path, notably the token-based club dashboard. A path prefix still wins,
  // so /nl/clubs?lang=it stays Dutch rather than becoming ambiguous.
  const searchLang = useRouterState({
    select: (s) => (s.location.search as { lang?: string } | undefined)?.lang,
  });
  const urlLocale = prefixMatch && LOCALIZED_LANGS.includes(prefixMatch[1] as Lang)
    ? (prefixMatch[1] as Lang)
    : searchLang && (LOCALIZED_LANGS.includes(searchLang as Lang) || searchLang === "en")
      ? (searchLang as Lang)
      : undefined;

  useEffect(() => {
    if (!__HF_DESIGN_INSPECTOR__) {
      return;
    }

    void import("../module/design-inspector/runtime")
      .then(({ installHiggsfieldDesignInspector }) => {
        installHiggsfieldDesignInspector();
      })
      .catch((error) => {
        reportHiggsfieldError(
          error instanceof Error ? error : new Error("Failed to load design inspector"),
          {
            boundary: "higgsfield_design_inspector_import",
          },
        );
      });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider urlLocale={urlLocale}>
        {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
        <Outlet />
        {/* Inside the provider, not in the shell's <body>. Rendered there it
            sat outside I18nProvider, so useI18n() fell back to English and a
            Dutch visitor was asked for consent in the wrong language. */}
        <Analytics />
        <ConsentBanner />
      </I18nProvider>
    </QueryClientProvider>
  );
}
